import assetsBase from '@exodus/assets-base'
import { create } from 'superstruct'
import { UnitType } from '@exodus/currency'
import { createPaymentMethodValidator } from '../payment-method.js'
import { Quote } from '../schema/quote.js'
import { ORDER_TYPES, PROVIDERS } from '../constants.js'
import { BuyOrder, SellOrder } from '../schema/order.js'
import { ENABLED_ASSETS, PAYMENT_METHODS } from './constants.js'

const assets = assetsBase.default || assetsBase

const fromProviderToExodusAsset = (asset, assets_ = ENABLED_ASSETS) => assets[assets_[asset]]

const fromExodusToProviderAsset = (asset, assets_ = ENABLED_ASSETS) =>
  Object.fromEntries(Object.entries(assets_).map((entry) => entry.reverse()))[asset]

const fromExodusToProviderPaymentMethod = (
  paymentMethod,
  { paymentMethods = PAYMENT_METHODS, ...parameters } = Object.create(null)
) => {
  const providerPaymentMethod = [...paymentMethods.values()].find(
    createPaymentMethodValidator({ ...parameters, type: paymentMethod })
  )

  return providerPaymentMethod?.id
}

const fromProviderToExodusPaymentMethod = (paymentMethod) => {
  if (!paymentMethod) {
    throw new Error('Payment method is required')
  }

  const type = PAYMENT_METHODS.get(paymentMethod)?.type
  return Array.isArray(type) ? type[0] : type
}

const fromProviderToExodusQuote = ({
  availablePaymentMethods = [],
  amount,
  fromAsset,
  input,
  payout = 0,
  paymentMethod,
  precisions,
  ramp: provider,
  rate = 1,
  toAsset,
  type,
  ...quote
}) => {
  const isBuy = type === ORDER_TYPES.buy
  const isSourceInput = input === 'source'
  const fromPrecision = precisions.get(fromAsset)
  const toPrecision = precisions.get(toAsset)
  const cryptoPrecision = isBuy ? toPrecision : fromPrecision
  const fiatPrecision = isBuy ? fromPrecision : toPrecision
  const cryptoUnit = UnitType.create({
    base: 0,
    [isBuy ? toAsset : fromAsset]: cryptoPrecision ?? 8,
  })

  const fiatUnit = UnitType.create({
    base: 0,
    [isBuy ? fromAsset : toAsset]: fiatPrecision ?? 2,
  })

  const cryptoAmount_ = isBuy ? (isSourceInput ? payout : amount) : isSourceInput ? amount : payout
  const fiatAmount_ = isBuy ? (isSourceInput ? amount : payout) : isSourceInput ? payout : amount
  const cryptoAmountUnit = cryptoUnit.defaultUnit(cryptoAmount_)
  const cryptoAmount = cryptoAmountUnit.toDefaultNumber()
  const fiatAmountUnit = fiatUnit.defaultUnit(fiatAmount_)
  const fiatAmount = fiatAmountUnit.toDefaultNumber()
  const feeUnit = fiatUnit.defaultUnit(quote.transactionFee ?? 0)
  const networkFeeUnit = fiatUnit.defaultUnit(quote.networkFee ?? 0)
  const totalFeeFiat = feeUnit.add(networkFeeUnit).toDefaultNumber()
  const totalFeeCrypto = cryptoUnit
    .defaultUnit((cryptoAmount * totalFeeFiat) / fiatAmount)
    .toDefaultNumber()

  const { max: maxFiat = Number.MAX_SAFE_INTEGER, min: minFiat = 0 } =
    availablePaymentMethods.find(({ paymentTypeId }) => paymentTypeId === paymentMethod)?.details
      ?.limits?.[provider] ?? Object.create(null)

  const maxCrypto =
    maxFiat === Number.MAX_SAFE_INTEGER || rate === 1
      ? Number.MAX_SAFE_INTEGER
      : cryptoUnit.defaultUnit(maxFiat / rate).toDefaultNumber()

  const minCrypto =
    minFiat === 0 || rate === 1 ? 0 : cryptoUnit.defaultUnit(minFiat / rate).toDefaultNumber()

  return create(
    {
      extraFee: 0,
      fee: feeUnit.toDefaultNumber(),
      from: {
        amount: isBuy ? fiatAmount : cryptoAmount,
        asset: fromAsset,
        maxBuyAmount: isBuy ? maxFiat : maxCrypto,
        minBuyAmount: isBuy ? minFiat : minCrypto,
        precision: fromPrecision,
      },
      metaProvider: PROVIDERS.onramper,
      networkFee: networkFeeUnit.toDefaultNumber(),
      paymentMethod,
      provider,
      rate: Number(rate.toFixed(fiatPrecision)),
      to: {
        amount: isBuy ? cryptoAmount : fiatAmount,
        asset: toAsset,
        maxBuyAmount: isBuy ? maxCrypto : maxFiat,
        minBuyAmount: isBuy ? minCrypto : minFiat,
        precision: toPrecision,
      },
      totalAmount: fiatAmount,
      totalFee: totalFeeFiat,
      totalFeeCrypto,
      type,
    },
    Quote
  )
}

const fromProviderToExodusOrder = (
  {
    createdAt,
    id,
    inAmount: fromAmount,
    outAmount: toAmount,
    onramp: subProvider,
    onrampTransactionId: subProviderId,
    paymentMethod,
    sourceCurrency,
    status,
    statusDate: updatedAt,
    targetCurrency,
    transactionHash: txId,
    type = ORDER_TYPES.buy,
    walletAddress,
  } = Object.create(null),
  assets_ = ENABLED_ASSETS
) => {
  const isBuy = type === ORDER_TYPES.buy
  const asset = fromProviderToExodusAsset(isBuy ? targetCurrency : sourceCurrency, assets_)

  const [cryptoAmount, fiatAmount] = isBuy ? [toAmount, fromAmount] : [fromAmount, toAmount]
  const fromAsset = isBuy ? sourceCurrency.toUpperCase() : asset.name
  const toAsset = isBuy ? asset.name : sourceCurrency.toUpperCase()
  const fiatAsset = (isBuy ? fromAsset : toAsset).toUpperCase()
  const fiatUnit = UnitType.create({
    base: 0,
    [fiatAsset]: 2,
  })

  const rateFiat = fiatUnit.defaultUnit(fiatAmount / cryptoAmount).toDefaultNumber()

  return create(
    {
      createdAt: new Date(createdAt ?? updatedAt),
      from: {
        amount: fromAmount,
        asset: fromAsset,
      },
      id,
      paymentMethod,
      paymentMethodType: fromProviderToExodusPaymentMethod(paymentMethod),
      provider: PROVIDERS.onramper,
      providerId: id,
      rate: rateFiat,
      status,
      subProvider,
      subProviderId,
      to: {
        amount: toAmount,
        asset: toAsset,
      },
      txId,
      updatedAt: new Date(updatedAt),
      walletAddress,
    },
    isBuy ? BuyOrder : SellOrder
  )
}

const onramperAdapter = {
  fromProviderToExodusAsset,
  fromExodusToProviderAsset,
  fromExodusToProviderPaymentMethod,
  fromProviderToExodusOrder,
  fromProviderToExodusPaymentMethod,
  fromProviderToExodusQuote,
}

export default onramperAdapter
